import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronLeft, Printer, Sparkles, Loader2, Globe, ShieldAlert, Download,
  RotateCcw, Image as ImageIcon, Check, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import BlueprintMark from '@/components/BlueprintMark';
import ProviderBadge from '@/components/ProviderBadge';
import TemplateEditorial from '@/components/website-templates/TemplateEditorial';
import TemplateBold from '@/components/website-templates/TemplateBold';
import TemplateShowcase from '@/components/website-templates/TemplateShowcase';
import SocialPostCard from '@/components/SocialPostCard';
import { PhoneFrame, APP_SCREENS } from '@/components/AppShellPreview';
import { nearestColorName } from '@/lib/colorNames';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODULE_TITLES = {
  naming: 'Naming',
  strategy: 'Strategy',
  brand: 'Brand Kit',
  logo: 'Logo',
  website: 'Website & App',
  social: 'Social & Reels',
  assets: 'Assets',
};

const VARIANT_LABELS = [
  { key: 'primary', label: 'Primary' },
  { key: 'mono', label: 'Mono' },
  { key: 'reversed', label: 'Reversed', dark: true },
  { key: 'icon', label: 'Icon' },
  { key: 'favicon', label: 'Favicon' },
];

const CASE_STUDY_PERMISSIONS = [
  { id: 'portfolio', label: 'Blueprints may reference this project in their portfolio' },
  { id: 'case_study', label: 'Blueprints may publish a written case study including project details and outcomes' },
  { id: 'visuals', label: 'Blueprints may use project visuals in marketing materials (social, website, ads)' },
  { id: 'testimonial', label: "Blueprints may quote the client's testimonial with attribution" },
];

const SectionCard = ({ id, eyebrow, title, action, children }) => (
  <section id={id} className="report-section bg-card border border-border p-8 mb-8">
    <div className="flex items-start justify-between gap-4 mb-6">
      <div>
        {eyebrow && <p className="font-mono text-xs uppercase tracking-widest text-primary/70 mb-1">{eyebrow}</p>}
        {title && <h2 className="font-heading text-2xl text-foreground">{title}</h2>}
      </div>
      {action}
    </div>
    {children}
  </section>
);

const EmptyNote = ({ children }) => (
  <p className="text-sm text-muted-foreground italic">{children}</p>
);

const ColorSwatch = ({ label, color, technical }) => {
  if (!color?.hex) return null;
  return (
    <div className="flex-1 min-w-[130px]">
      <div className="w-full aspect-square border border-border" style={{ background: color.hex }} />
      <p className="text-sm font-semibold text-foreground mt-2">{label}</p>
      {technical ? (
        <div className="font-mono text-[11px] text-muted-foreground leading-relaxed">
          <p>{color.hex}</p>
          {color.rgb && <p>{color.rgb}</p>}
          {color.cmyk && <p>CMYK {color.cmyk}</p>}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">{nearestColorName(color.hex)}</p>
      )}
    </div>
  );
};

const BrowserFrame = ({ label, children }) => (
  <div className="border border-border">
    <div className="flex items-center gap-2 px-4 py-2.5 bg-black/[0.04] border-b border-border">
      <div className="flex gap-1.5">
        <span className="w-2.5 h-2.5 rounded-full bg-black/15" />
        <span className="w-2.5 h-2.5 rounded-full bg-black/15" />
        <span className="w-2.5 h-2.5 rounded-full bg-black/15" />
      </div>
      <div className="flex-1 mx-4 px-3 py-1 bg-black/[0.04] text-xs text-muted-foreground text-center truncate">
        {label}
      </div>
    </div>
    {children}
  </div>
);

// ─── Handover-specific sub-components ───────────────────────────────────────

const StatusIcon = ({ ok }) =>
  ok ? (
    <Check className="w-4 h-4 text-emerald-600 shrink-0" />
  ) : (
    <X className="w-4 h-4 text-muted-foreground/40 shrink-0" />
  );

const CheckpointRow = ({ label, status }) => {
  const ok = status === 'complete' || status === 'approved' || status === 'done' || status === true;
  const amber = status === 'in_progress' || status === 'partial';
  return (
    <div className="flex items-center gap-3 py-2 border-b border-border last:border-0">
      {ok ? (
        <Check className="w-4 h-4 text-emerald-600 shrink-0" />
      ) : amber ? (
        <span className="w-4 h-4 text-amber-500 shrink-0 font-mono text-xs flex items-center justify-center">—</span>
      ) : (
        <X className="w-4 h-4 text-red-400 shrink-0" />
      )}
      <span className="text-sm text-foreground">{label}</span>
      <span className="ml-auto font-mono text-xs text-muted-foreground capitalize">{String(status || 'pending')}</span>
    </div>
  );
};

// ─── Main component ─────────────────────────────────────────────────────────

const ReportPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const section = searchParams.get('section');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('technical'); // 'technical' | 'client' | 'handover'
  const [narrative, setNarrative] = useState(null);
  const [narrativeLoading, setNarrativeLoading] = useState(false);

  // Handover state
  const [checkpoints, setCheckpoints] = useState([]);
  const [checkpointsLoading, setCheckpointsLoading] = useState(false);
  const [caseStudyClientName, setCaseStudyClientName] = useState('');
  const [caseStudyPermissions, setCaseStudyPermissions] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/projects/${projectId}/report/data`, { withCredentials: true });
        setData(res.data);
        setNarrative(res.data.narrative);
      } catch (error) {
        toast.error('Failed to load report data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [projectId]);

  useEffect(() => {
    if (mode !== 'handover' || !projectId) return;
    const loadCheckpoints = async () => {
      try {
        setCheckpointsLoading(true);
        const res = await axios.get(`${API}/projects/${projectId}/checkpoints`, { withCredentials: true });
        setCheckpoints(res.data || []);
      } catch {
        // Checkpoints endpoint may not be configured yet
      } finally {
        setCheckpointsLoading(false);
      }
    };
    loadCheckpoints();
  }, [mode, projectId]);

  const generateNarrative = async () => {
    try {
      setNarrativeLoading(true);
      const res = await axios.post(`${API}/projects/${projectId}/report/narrative/generate`);
      setNarrative(res.data);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Failed to generate the narrative');
    } finally {
      setNarrativeLoading(false);
    }
  };

  const toggleCaseStudyPermission = (id) => {
    setCaseStudyPermissions((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const handleCaseStudyPrint = () => {
    document.body.classList.add('print-case-study');
    const cleanup = () => {
      document.body.classList.remove('print-case-study');
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const {
    project, brand_kit: brandKit, naming_candidates: naming = [],
    strategy, logo_concepts: logoConcepts = [], site_content: siteContent,
    assets = [], preparer, social_posts: socialPosts = [], video_reels: videoReels = [],
  } = data;

  const showAll = !section;
  const show = (id) => showAll || section === id;
  const technical = mode === 'technical';
  const handover = mode === 'handover';

  const shortlisted = naming.filter((c) => c.status === 'shortlisted');
  const namedCandidates = shortlisted.length > 0 ? shortlisted : naming;
  const selectedLogo = logoConcepts.find((c) => c.variants);

  const refNumber = `RPT-${project.id.slice(0, 8).toUpperCase()}-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}`;
  const now = new Date();
  const dateStamp = now.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  const timeStamp = now.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  const agencyName = preparer?.company_name || 'Blueprints';
  const hasClientContact = project.client_contact_name || project.client_email || project.client_phone || project.client_address;

  // ── Handover: derived data ──────────────────────────────────────────────
  const stage04Checkpoints = checkpoints.filter(
    (cp) => cp.stage === '04' || cp.stage === 4 || String(cp.stage_code || '').startsWith('04')
  );
  const trackACheckpoints = stage04Checkpoints.filter((cp) => cp.track === 'A' || cp.track === 'brand');
  const trackBCheckpoints = stage04Checkpoints.filter((cp) => cp.track === 'B' || cp.track === 'digital');
  const trackCCheckpoints = stage04Checkpoints.filter((cp) => cp.track === 'C' || cp.track === 'physical');
  const launchSignedOff = stage04Checkpoints.some(
    (cp) => cp.signed_off || cp.status === 'signed_off' || cp.status === 'approved'
  );
  const launchDate = stage04Checkpoints.find((cp) => cp.signed_off_at)?.signed_off_at
    ? new Date(stage04Checkpoints.find((cp) => cp.signed_off_at).signed_off_at).toLocaleDateString()
    : dateStamp;

  const hasLogoVariants = !!selectedLogo;
  const hasBrandGuidelines = !!(strategy && brandKit);
  const hasWebsite = !!siteContent;
  const hasApp = !!siteContent;
  const approvedPostsCount = socialPosts.filter((p) => p.status === 'approved').length;
  const hasReels = videoReels.length > 0;

  const assetRows = [
    { asset: 'Logo Variants', format: 'SVG + PNG (5 variants)', ok: hasLogoVariants, note: hasLogoVariants ? selectedLogo.direction : '' },
    { asset: 'Brand Guidelines', format: 'Digital (Brand Kit)', ok: hasBrandGuidelines, note: '' },
    { asset: 'Website Copy', format: 'JSON → CMS-ready', ok: hasWebsite, note: '' },
    { asset: 'Mobile App Shell', format: 'Screen designs', ok: hasApp, note: '' },
    { asset: 'Social Posts', format: 'Image + Caption', ok: approvedPostsCount > 0, note: approvedPostsCount > 0 ? `${approvedPostsCount} approved` : '' },
    { asset: 'Video Reels', format: 'MP4', ok: hasReels, note: hasReels ? `${videoReels.length} reel${videoReels.length !== 1 ? 's' : ''}` : '' },
  ];

  return (
    <div className="min-h-screen bg-background blueprint-grid">
      {/* Header -- hidden on print */}
      <header className="no-print sticky top-0 z-20 h-16 border-b border-border flex items-center justify-between px-6 bg-card">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(`/dashboard/${projectId}`)} data-testid="report-back-btn">
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <h1 className="font-heading font-semibold text-foreground text-lg hidden sm:block">
            {project.name} — {showAll ? 'Full Report' : `${MODULE_TITLES[section] || section} Report`}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex border border-border">
            <button
              onClick={() => setMode('technical')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${technical ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="report-mode-technical"
            >
              Technical
            </button>
            <button
              onClick={() => setMode('client')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${mode === 'client' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="report-mode-client"
            >
              Client
            </button>
            <button
              onClick={() => setMode('handover')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${handover ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="report-mode-handover"
            >
              Handover
            </button>
          </div>
          {!handover && (
            <Button onClick={() => window.print()} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="report-print-btn">
              <Printer className="w-4 h-4 mr-2" />
              Print / Save as PDF
            </Button>
          )}
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-8 print:p-0">
        {/* Cover / title block */}
        <div className="report-section border border-border bg-card mb-8">
          <div className="p-10 flex items-center justify-between gap-5 flex-wrap">
            <div className="flex items-center gap-5">
              {preparer?.company_logo ? (
                <img src={preparer.company_logo} alt={agencyName} className="w-14 h-14 object-contain shrink-0" />
              ) : (
                <BlueprintMark className="w-14 h-14 text-primary shrink-0" />
              )}
              <div>
                <p className="font-mono text-xs uppercase tracking-widest text-primary/70 mb-1">
                  {technical ? 'Technical Implementation Report' : handover ? 'Client Handover Package' : 'Your Brand, Explained'}
                </p>
                <h1 className="font-heading text-4xl text-foreground">{project.name}</h1>
                {project.industry && <p className="text-muted-foreground mt-1">{project.industry}</p>}
              </div>
            </div>
            {technical && (
              <span className="font-mono text-[11px] uppercase tracking-widest border border-red-500/30 text-red-700 bg-red-500/5 px-3 py-1.5">
                Internal Use Only — Confidential
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 border-t border-border font-mono text-xs">
            <div className="p-4 border-r border-border">
              <p className="text-muted-foreground/70 uppercase tracking-wide mb-1">Reference</p>
              <p className="text-foreground">{refNumber}</p>
            </div>
            <div className="p-4 border-r border-border">
              <p className="text-muted-foreground/70 uppercase tracking-wide mb-1">{technical ? 'Date / Time' : 'Date'}</p>
              <p className="text-foreground">{dateStamp}{technical ? ` · ${timeStamp}` : ''}</p>
            </div>
            <div className="p-4 border-r border-border">
              <p className="text-muted-foreground/70 uppercase tracking-wide mb-1">Prepared by</p>
              <p className="text-foreground">{agencyName}</p>
              {technical && preparer?.company_phone && <p className="text-muted-foreground">{preparer.company_phone}</p>}
            </div>
            <div className="p-4">
              <p className="text-muted-foreground/70 uppercase tracking-wide mb-1">Scope</p>
              <p className="text-foreground">{handover ? 'Handover Package' : showAll ? 'Full Project' : MODULE_TITLES[section] || section}</p>
            </div>
          </div>
          {hasClientContact && (
            <div className="grid grid-cols-2 md:grid-cols-4 border-t border-border font-mono text-xs">
              <div className="p-4 border-r border-border col-span-2 md:col-span-1">
                <p className="text-muted-foreground/70 uppercase tracking-wide mb-1">Prepared for</p>
                <p className="text-foreground">{project.client_contact_name || project.name}</p>
              </div>
              {technical && project.client_email && (
                <div className="p-4 border-r border-border">
                  <p className="text-muted-foreground/70 uppercase tracking-wide mb-1">Client Email</p>
                  <p className="text-foreground">{project.client_email}</p>
                </div>
              )}
              {technical && project.client_phone && (
                <div className="p-4 border-r border-border">
                  <p className="text-muted-foreground/70 uppercase tracking-wide mb-1">Client Phone</p>
                  <p className="text-foreground">{project.client_phone}</p>
                </div>
              )}
              {technical && project.client_address && (
                <div className="p-4">
                  <p className="text-muted-foreground/70 uppercase tracking-wide mb-1">Client Address</p>
                  <p className="text-foreground">{project.client_address}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── HANDOVER TAB CONTENT ───────────────────────────────────────── */}
        {handover && (
          <div className="space-y-8">
            {/* Section A: Launch Readiness (BP-04-A) */}
            <section className="report-section bg-card border border-border p-8 no-handover-print">
              <p className="font-mono text-xs uppercase tracking-widest text-primary/70 mb-1">Stage 04</p>
              <h2 className="font-heading text-2xl text-foreground mb-6">Launch Readiness (BP-04-A)</h2>

              {launchSignedOff && (
                <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 p-4 mb-6">
                  <Check className="w-5 h-5 text-emerald-600 shrink-0" />
                  <span className="font-mono text-sm text-emerald-700 uppercase tracking-wide">
                    LAUNCH APPROVED — {launchDate}
                  </span>
                </div>
              )}

              {checkpointsLoading ? (
                <div className="flex items-center gap-2 py-4">
                  <Loader2 className="w-4 h-4 animate-spin text-primary" />
                  <span className="text-sm text-muted-foreground">Loading checkpoints…</span>
                </div>
              ) : stage04Checkpoints.length === 0 ? (
                <EmptyNote>No Stage 04 checkpoints recorded yet.</EmptyNote>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[
                    { label: 'Track A — Brand', items: trackACheckpoints },
                    { label: 'Track B — Digital', items: trackBCheckpoints },
                    { label: 'Track C — Physical', items: trackCCheckpoints },
                  ].map(({ label, items }) => (
                    <div key={label}>
                      <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground mb-3">{label}</p>
                      {items.length === 0 ? (
                        <p className="text-xs text-muted-foreground italic">No items</p>
                      ) : (
                        items.map((cp, i) => (
                          <CheckpointRow key={cp.id || i} label={cp.label || cp.name || `Checkpoint ${i + 1}`} status={cp.status} />
                        ))
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Section B: Asset Delivery Manifest */}
            <section className="report-section bg-card border border-border p-8 no-handover-print">
              <p className="font-mono text-xs uppercase tracking-widest text-primary/70 mb-1">Deliverables</p>
              <h2 className="font-heading text-2xl text-foreground mb-6">Asset Delivery Manifest</h2>

              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left font-mono text-xs uppercase tracking-wide text-muted-foreground py-2 pr-4">Asset</th>
                      <th className="text-left font-mono text-xs uppercase tracking-wide text-muted-foreground py-2 pr-4">Format</th>
                      <th className="text-left font-mono text-xs uppercase tracking-wide text-muted-foreground py-2 pr-4">Status</th>
                      <th className="text-left font-mono text-xs uppercase tracking-wide text-muted-foreground py-2">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assetRows.map((row) => (
                      <tr key={row.asset} className="border-b border-border last:border-0">
                        <td className="py-3 pr-4 font-medium text-foreground">{row.asset}</td>
                        <td className="py-3 pr-4 text-muted-foreground font-mono text-xs">{row.format}</td>
                        <td className="py-3 pr-4">
                          <div className="flex items-center gap-1.5">
                            <StatusIcon ok={row.ok} />
                            <span className={row.ok ? 'text-emerald-600' : 'text-muted-foreground/50'}>
                              {row.ok ? 'Ready' : 'Not ready'}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 text-muted-foreground text-xs">{row.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Section C: 30-Day Post-Launch Support Terms */}
            <section className="report-section bg-card border border-border p-8 no-handover-print">
              <p className="font-mono text-xs uppercase tracking-widest text-primary/70 mb-1">Support</p>
              <h2 className="font-heading text-2xl text-foreground mb-6">30-Day Post-Launch Support Terms</h2>

              <div className="border-l-4 border-[#C5A059] bg-background p-5">
                <p className="text-sm text-foreground leading-relaxed">
                  Blueprints provides 30 days of post-launch support from the date of site go-live. Covered: bug fixes
                  on delivered work, content updates (up to 3 per week), DNS/email configuration assistance, and guidance
                  on platform use. Not covered: new features, redesigns, additional pages, or scope outside the original
                  project brief. Changes beyond this scope are quoted separately as a new project or change request.
                  Support requests via: <span className="font-semibold">hello@blueprints.net.au</span> or the shared
                  Slack channel.
                </p>
              </div>
            </section>

            {/* Section D: Case Study Permission (printable form) */}
            <section className="report-section bg-card border border-border p-8 handover-case-study" data-testid="case-study-section">
              <p className="font-mono text-xs uppercase tracking-widest text-primary/70 mb-1">Authorisation</p>
              <h2 className="font-heading text-2xl text-foreground mb-6">Case Study Permission</h2>

              <div className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">Project Name</p>
                    <p className="text-foreground font-heading text-lg">{project.name}</p>
                  </div>
                  <div className="space-y-1">
                    <label className="font-mono text-xs uppercase tracking-wide text-muted-foreground block">Client Name</label>
                    <input
                      type="text"
                      value={caseStudyClientName}
                      onChange={(e) => setCaseStudyClientName(e.target.value)}
                      placeholder={project.client_contact_name || 'Enter client name'}
                      className="no-print w-full rounded-md bg-black/[0.03] border border-border text-foreground placeholder:text-muted-foreground/70 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                    />
                    <p className="hidden print-show font-heading text-lg text-foreground">
                      {caseStudyClientName || project.client_contact_name || '____________________________'}
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">Permissions</p>
                  {CASE_STUDY_PERMISSIONS.map((perm) => (
                    <label key={perm.id} className="flex items-start gap-3 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={caseStudyPermissions.includes(perm.id)}
                        onChange={() => toggleCaseStudyPermission(perm.id)}
                        className="accent-primary mt-0.5 shrink-0"
                      />
                      <span className="text-sm text-foreground">{perm.label}</span>
                    </label>
                  ))}
                </div>

                <div className="border-t border-border pt-5 space-y-4">
                  <div className="grid grid-cols-2 gap-8">
                    <div>
                      <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground mb-3">Authorised by</p>
                      <div className="border-b border-foreground/30 h-8" />
                    </div>
                    <div>
                      <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground mb-3">Date</p>
                      <div className="border-b border-foreground/30 h-8" />
                    </div>
                  </div>
                </div>

                <div className="no-print flex items-center gap-3 pt-2">
                  <Button
                    onClick={handleCaseStudyPrint}
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="case-study-print-btn"
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Print / Save as PDF
                  </Button>
                  <p className="text-xs text-muted-foreground">Prints this section only.</p>
                </div>
              </div>
            </section>
          </div>
        )}

        {/* ── STANDARD REPORT CONTENT (technical / client modes) ────────── */}
        {!handover && (
          <>
            {/* Client narrative -- AI-drafted, client mode only, whole-project reports only */}
            {!technical && showAll && (
              <SectionCard
                eyebrow="Why we made these choices"
                title="A Note on Your Brand"
                action={
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={generateNarrative}
                    disabled={narrativeLoading}
                    className="no-print bg-black/[0.03] border-border text-foreground hover:bg-black/[0.06]"
                    data-testid="report-narrative-generate-btn"
                  >
                    {narrativeLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1.5" />}
                    {narrative ? 'Regenerate' : 'Generate'}
                  </Button>
                }
              >
                {narrative?.narrative ? (
                  <>
                    <p className="text-foreground leading-relaxed whitespace-pre-wrap font-heading text-lg">{narrative.narrative}</p>
                    <ProviderBadge provider={narrative.generated_by} className="block mt-4" />
                  </>
                ) : (
                  <EmptyNote>No narrative yet — click Generate to have Blueprints explain the reasoning behind your brand in plain language.</EmptyNote>
                )}
              </SectionCard>
            )}

            {/* Naming */}
            {show('naming') && (
              <SectionCard id="naming" eyebrow="Phase 2" title="Naming">
                {namedCandidates.length === 0 ? (
                  <EmptyNote>No name candidates generated yet.</EmptyNote>
                ) : technical ? (
                  <div className="space-y-4">
                    {namedCandidates.map((c) => (
                      <div key={c.id} className="border border-border p-4">
                        <div className="flex items-center justify-between gap-3 mb-1">
                          <h3 className="font-heading text-xl text-foreground">{c.name}</h3>
                          <span className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">{c.status}</span>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">{c.rationale}</p>
                        <div className="flex flex-wrap gap-2 mb-2">
                          {(c.domains || []).map((d) => (
                            <span key={d.tld} className="inline-flex items-center gap-1 px-2 py-1 font-mono text-xs border border-border text-muted-foreground">
                              <Globe className="w-3 h-3" />.{d.tld} — {d.available === true ? 'available' : d.available === false ? 'taken' : 'unknown'}
                            </span>
                          ))}
                          <span className="inline-flex items-center gap-1 px-2 py-1 font-mono text-xs border border-border text-muted-foreground">
                            <ShieldAlert className="w-3 h-3" />{c.trademark_risk} risk
                          </span>
                        </div>
                        <ProviderBadge provider={c.generated_by} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {namedCandidates.slice(0, 3).map((c) => (
                      <div key={c.id}>
                        <h3 className="font-heading text-3xl text-foreground">{c.name}</h3>
                        <p className="text-muted-foreground">{c.rationale}</p>
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>
            )}

            {/* Strategy */}
            {show('strategy') && (
              <SectionCard id="strategy" eyebrow="Phase 3" title="Strategy">
                {!strategy ? (
                  <EmptyNote>No strategy generated yet.</EmptyNote>
                ) : (
                  <div className="space-y-6">
                    <div>
                      <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground mb-2">Positioning</p>
                      <p className="text-foreground text-lg leading-relaxed font-heading">{strategy.positioning_statement}</p>
                    </div>

                    {strategy.personas?.length > 0 && (
                      <div>
                        <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground mb-2">Who we're speaking to</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {strategy.personas.map((p, i) => (
                            <div key={i} className="border border-border p-4">
                              <h4 className="font-heading text-foreground mb-1">{p.name}</h4>
                              <p className="text-sm text-muted-foreground mb-2">{p.description}</p>
                              {technical && p.pain_points?.length > 0 && (
                                <>
                                  <p className="text-xs uppercase tracking-wide text-muted-foreground/70">Pain points</p>
                                  <ul className="text-sm text-foreground list-disc list-inside mb-2">
                                    {p.pain_points.map((pt, j) => <li key={j}>{pt}</li>)}
                                  </ul>
                                </>
                              )}
                              {technical && p.goals?.length > 0 && (
                                <>
                                  <p className="text-xs uppercase tracking-wide text-muted-foreground/70">Goals</p>
                                  <ul className="text-sm text-foreground list-disc list-inside">
                                    {p.goals.map((g, j) => <li key={j}>{g}</li>)}
                                  </ul>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {technical && strategy.competitive_landscape?.length > 0 && (
                      <div>
                        <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground mb-2">Competitive Landscape</p>
                        <div className="space-y-3">
                          {strategy.competitive_landscape.map((c, i) => (
                            <div key={i} className="border border-border p-4">
                              <h4 className="font-heading text-foreground mb-1">{c.name}</h4>
                              <p className="text-sm text-muted-foreground"><span className="text-foreground/80">Strengths:</span> {c.strengths}</p>
                              <p className="text-sm text-muted-foreground"><span className="text-foreground/80">Weaknesses:</span> {c.weaknesses}</p>
                              <p className="text-sm text-muted-foreground"><span className="text-foreground/80">Differentiation:</span> {c.differentiation}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {strategy.content_pillars?.length > 0 && (
                      <div>
                        <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground mb-2">Content Pillars</p>
                        <div className="flex flex-wrap gap-2">
                          {strategy.content_pillars.map((pillar, i) => (
                            <span key={i} className="px-3 py-1.5 border border-border text-sm text-foreground">{pillar}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {technical && <ProviderBadge provider={strategy.generated_by} />}
                  </div>
                )}
              </SectionCard>
            )}

            {/* Brand Kit */}
            {show('brand') && (
              <SectionCard id="brand" eyebrow="Phase 1" title="Brand Kit">
                {!brandKit ? (
                  <EmptyNote>No brand kit saved yet.</EmptyNote>
                ) : (
                  <div className="space-y-8">
                    <div>
                      <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground mb-3">Colors</p>
                      <div className="flex flex-wrap gap-5">
                        <ColorSwatch label="Primary" color={brandKit.colors?.primary} technical={technical} />
                        <ColorSwatch label="Secondary" color={brandKit.colors?.secondary} technical={technical} />
                        <ColorSwatch label="Accent" color={brandKit.colors?.accent} technical={technical} />
                        {technical && <ColorSwatch label="Background" color={brandKit.colors?.background} technical={technical} />}
                        {technical && <ColorSwatch label="Foreground" color={brandKit.colors?.foreground} technical={technical} />}
                      </div>
                      {technical && brandKit.forbidden_colors?.length > 0 && (
                        <p className="text-xs text-muted-foreground mt-4">
                          <span className="font-semibold text-foreground">Never use:</span> {brandKit.forbidden_colors.join(', ')}
                        </p>
                      )}
                    </div>

                    <div>
                      <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground mb-3">Typography</p>
                      <p className="text-3xl text-foreground mb-2" style={{ fontFamily: brandKit.typography?.heading_font }}>
                        {brandKit.business_name || project.name}
                      </p>
                      <p className="text-base text-muted-foreground" style={{ fontFamily: brandKit.typography?.body_font }}>
                        Body text will look like this. Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                      </p>
                      {technical && (
                        <p className="font-mono text-xs text-muted-foreground mt-3">
                          Heading: {brandKit.typography?.heading_font} · Body: {brandKit.typography?.body_font}
                          {brandKit.typography?.mono_font && ` · Mono: ${brandKit.typography.mono_font}`}
                        </p>
                      )}
                    </div>

                    {brandKit.tone_of_voice && (
                      <div>
                        <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground mb-2">Voice</p>
                        <p className="text-foreground">{brandKit.tone_of_voice}</p>
                      </div>
                    )}

                    {brandKit.visual_keywords?.length > 0 && (
                      <div>
                        <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground mb-2">Keywords</p>
                        <div className="flex flex-wrap gap-2">
                          {brandKit.visual_keywords.map((k, i) => (
                            <span key={i} className="px-3 py-1.5 border border-border text-sm text-foreground">{k}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {technical && brandKit.spacing && (
                      <div>
                        <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground mb-2">Spacing / Radius Tokens</p>
                        <p className="font-mono text-xs text-muted-foreground">
                          unit {brandKit.spacing.unit}px · radius sm/md/lg {brandKit.spacing.radius_sm}/{brandKit.spacing.radius_md}/{brandKit.spacing.radius_lg}px
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </SectionCard>
            )}

            {/* Logo */}
            {show('logo') && (
              <SectionCard id="logo" eyebrow="Phase 4" title="Logo">
                {!selectedLogo ? (
                  logoConcepts.length > 0 ? (
                    <EmptyNote>{logoConcepts.length} concept(s) generated — none finalized into variants yet.</EmptyNote>
                  ) : (
                    <EmptyNote>No logo concepts generated yet.</EmptyNote>
                  )
                ) : (
                  <div>
                    <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground mb-4">{selectedLogo.direction}</p>
                    <div className="grid grid-cols-5 gap-3 mb-3">
                      {VARIANT_LABELS.map(({ key, label, dark }) => (
                        <div key={key} className="text-center">
                          <div className={`aspect-square border border-border flex items-center justify-center overflow-hidden mb-1 ${dark ? 'bg-[#1B3A5C]' : 'bg-white'}`}>
                            {selectedLogo.variants[key] && (
                              <img src={selectedLogo.variants[key]} alt={label} className="w-full h-full object-contain p-1" />
                            )}
                          </div>
                          <span className="text-[11px] text-muted-foreground">{label}</span>
                        </div>
                      ))}
                    </div>
                    {technical && (
                      <div className="flex items-center gap-4 mt-3">
                        {selectedLogo.variants.svg && (
                          <a
                            href={`data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(selectedLogo.variants.svg)))}`}
                            download={`logo-${selectedLogo.direction.toLowerCase().replace(/\s+/g, '-')}.svg`}
                            className="no-print inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
                          >
                            <Download className="w-3 h-3" />
                            Download SVG source
                          </a>
                        )}
                        <ProviderBadge provider={selectedLogo.generated_by} />
                      </div>
                    )}
                  </div>
                )}
              </SectionCard>
            )}

            {/* Website + App */}
            {show('website') && (
              <SectionCard id="website" eyebrow="Phases 5–6" title="Website & App">
                {!siteContent ? (
                  <EmptyNote>No website/app copy generated yet.</EmptyNote>
                ) : (
                  <div className="space-y-8">
                    <div>
                      <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground mb-3">
                        {technical ? 'Website concepts (live-rendered from Brand Kit tokens)' : 'How your website looks'}
                      </p>
                      <div className="space-y-6">
                        {[TemplateEditorial, TemplateBold, TemplateShowcase].map((Template, i) => (
                          <BrowserFrame key={i} label={(brandKit?.business_name || project.name || 'yoursite').toLowerCase().replace(/[^a-z0-9]+/g, '') + '.com'}>
                            <Template brandKit={brandKit} content={siteContent} />
                          </BrowserFrame>
                        ))}
                      </div>
                    </div>

                    <div>
                      <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground mb-3">
                        {technical ? 'App screens (live-rendered, non-interactive)' : 'How your app looks'}
                      </p>
                      <div className="flex flex-wrap gap-6">
                        {APP_SCREENS.map((s) => (
                          <div key={s.id} className="text-center">
                            <PhoneFrame screenId={s.id} brandKit={brandKit} content={siteContent} width={150} />
                            <p className="text-xs text-muted-foreground mt-2">{s.label}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground mb-3">
                        {technical ? 'Copy (component mapping)' : 'Your message'}
                      </p>
                      {!technical && strategy?.positioning_statement && (
                        <p className="text-sm text-muted-foreground mb-4 italic">
                          Every line below reflects your positioning: &ldquo;{strategy.positioning_statement}&rdquo;
                        </p>
                      )}
                      <div className="space-y-3 text-sm">
                        <p><span className="font-semibold text-foreground">{technical ? 'Hero eyebrow: ' : ''}</span>{siteContent.eyebrow}</p>
                        <p><span className="font-semibold text-foreground">{technical ? 'Hero headline: ' : ''}</span>{siteContent.headline}</p>
                        <p><span className="font-semibold text-foreground">{technical ? 'Hero subheadline: ' : ''}</span>{siteContent.subheadline}</p>
                        <p><span className="font-semibold text-foreground">{siteContent.about_title}: </span>{siteContent.about_body}</p>
                        {siteContent.services?.map((s, i) => (
                          <p key={i}><span className="font-semibold text-foreground">{s.title}: </span>{s.description}</p>
                        ))}
                        <p><span className="font-semibold text-foreground">{siteContent.cta_headline}: </span>{siteContent.cta_body} <span className="italic">[{siteContent.cta_button_text}]</span></p>
                        <p className="italic">&ldquo;{siteContent.testimonial_quote}&rdquo; — {siteContent.testimonial_author}</p>
                        <p className="text-muted-foreground">{siteContent.footer_tagline}</p>
                      </div>
                      {technical && <ProviderBadge provider={siteContent.generated_by} className="block mt-3" />}
                    </div>
                  </div>
                )}
              </SectionCard>
            )}

            {/* Social & Reels */}
            {show('social') && (
              <SectionCard id="social" eyebrow="Phase 7" title="Social & Reels">
                {socialPosts.length === 0 && videoReels.length === 0 ? (
                  <EmptyNote>No social posts or reels generated yet.</EmptyNote>
                ) : (
                  <div className="space-y-8">
                    {socialPosts.filter((p) => p.status !== 'rejected').length > 0 && (
                      <div>
                        <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground mb-4">Post concepts</p>
                        <div className="flex flex-wrap gap-6">
                          {socialPosts.filter((p) => p.status !== 'rejected').map((post) => (
                            <div key={post.id}>
                              <SocialPostCard post={post} brandKit={brandKit} businessName={project.name} />
                              {technical && <ProviderBadge provider={post.generated_by} className="block mt-2" />}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {videoReels.length > 0 && (
                      <div>
                        <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground mb-4">Reels</p>
                        <div className="flex flex-wrap gap-5">
                          {videoReels.map((reel) => (
                            <div key={reel.id} className="text-center">
                              <div className="rounded-[1.5rem] p-2 bg-[#111318]" style={{ width: '150px' }}>
                                <div className="rounded-[1.1rem] overflow-hidden bg-black" style={{ aspectRatio: '9/16' }}>
                                  <video src={reel.video_url} controls loop className="w-full h-full object-cover" />
                                </div>
                              </div>
                              <p className="text-[11px] text-muted-foreground mt-2 max-w-[150px]">{reel.caption}</p>
                              {technical && <ProviderBadge provider={reel.generated_by} />}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </SectionCard>
            )}

            {/* Assets */}
            {show('assets') && (
              <SectionCard id="assets" eyebrow="Asset Library" title="All Assets">
                {assets.length === 0 ? (
                  <EmptyNote>No assets generated yet.</EmptyNote>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {assets.map((asset) => (
                      <div key={asset.id} className="border border-border">
                        <div className="aspect-square bg-black/[0.03] flex items-center justify-center overflow-hidden">
                          {asset.url ? (
                            <img src={asset.url} alt={asset.prompt} className="w-full h-full object-cover" />
                          ) : (
                            <ImageIcon className="w-6 h-6 text-muted-foreground/50" />
                          )}
                        </div>
                        <div className="p-2">
                          <p className="text-xs font-semibold text-foreground capitalize">{asset.asset_type}</p>
                          {technical && <p className="text-[11px] text-muted-foreground truncate" title={asset.prompt}>{asset.prompt}</p>}
                          {technical && <ProviderBadge provider={asset.generated_by} />}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>
            )}
          </>
        )}

        {/* Footer */}
        <div className="report-section border-t border-border pt-6 pb-10">
          <div className="flex items-center justify-between gap-4 flex-wrap text-xs text-muted-foreground mb-3">
            <div className="flex items-center gap-2">
              {preparer?.company_logo ? (
                <img src={preparer.company_logo} alt={agencyName} className="w-4 h-4 object-contain" />
              ) : (
                <BlueprintMark className="w-4 h-4" />
              )}
              <span>{technical ? `Prepared by ${agencyName}` : `Powered by ${agencyName}`}</span>
            </div>
            <span>{refNumber} · Generated {dateStamp}</span>
          </div>
          {(preparer?.company_website || preparer?.company_email || preparer?.company_phone || preparer?.company_whatsapp) && (
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-muted-foreground font-mono">
              {preparer?.company_website && <span>Website: {preparer.company_website}</span>}
              {preparer?.company_email && <span>Email: {preparer.company_email}</span>}
              {preparer?.company_phone && <span>Phone: {preparer.company_phone}</span>}
              {preparer?.company_whatsapp && <span>WhatsApp: {preparer.company_whatsapp}</span>}
            </div>
          )}
          {!technical && (preparer?.company_email || preparer?.company_phone) && (
            <p className="text-xs text-muted-foreground mt-3 italic">
              Questions about this? Reach us at {[preparer.company_email, preparer.company_phone].filter(Boolean).join(' or ')}.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportPage;
